const rmsFetch = require('./fetch')
const { transformRmsShipment } = require('../tools/transform-rms-shipment')
const { validateStockDataOptions } = require('../tools/validate-options')
const keyBy = require('lodash/keyBy')

class RmsAdapter {
  constructor (rmsApiConfigs = {}, fetch, pgConnection, logger, api, user) {
    if (!rmsApiConfigs || !rmsApiConfigs.username || !rmsApiConfigs.password || !rmsApiConfigs.baseUrl) {
      // If no configs provided, skip
      return
    }

    this.interface = rmsApiConfigs.interface || 'rms'
    this.username = rmsApiConfigs.username
    this.password = rmsApiConfigs.password
    this.baseUrl = rmsApiConfigs.baseUrl
    this.authUrl = rmsApiConfigs.authUrl || `${this.baseUrl}/api/auth/validate`

    this.logger = logger
    this._rawFetch = fetch
    this.pgConnection = pgConnection
    this.api = api
    this.user = user

    if (typeof fetch !== 'function') {
      throw new Error('fetch is expected as an argument')
    }
  }

  async getAuthedFetch () {
    if (!this.username || !this.password || !this.baseUrl) {
      this.logger.warn('RmsAdapter: no configs provided')
    }

    const token = await this.getAccessToken()

    if (!token) {
      throw new Error('RmsAdapter: no token available')
    }

    // Build a fresh authed fetch with the current token
    return rmsFetch({ token, fetch: this._rawFetch, logger: this.logger, baseUrl: this.baseUrl })
  }

  async getAccessToken () {
    // Get valid token from DB if available
    const existing = await this.selectValidToken()
    if (existing) return existing.token

    // If not request a new token
    const fresh = await this.requestNewToken()

    if (!fresh) return null

    // Store new token in DB
    await this.insertToken(fresh)
    return fresh.token
  }

  /**
   * Look up the most recent, non-expired token for this.interface
   */
  async selectValidToken () {
    if (!this.pgConnection) {
      this.logger.warn('RmsAdapter: PG client not provided; cannot read tokens')
      return null
    }

    const query = `
      SELECT id, interface, token, expiry, created_at, created_by, updated_at, updated_by
      FROM avocado.data_accesstokens
      WHERE interface = $1 AND expiry > NOW()
      ORDER BY expiry DESC
      LIMIT 1
    `
    const values = [this.interface]
    try {
      const { rows } = await this.pgConnection.query(query, values)
      return rows[0] || null
    } catch (err) {
      this.logger.error('RmsAdapter: token select failed', err)
      return null
    }
  }

  async insertToken ({ token, expiry }) {
    if (!this.pgConnection) {
      this.logger.warn('RmsAdapter: DB client not provided; cannot persist tokens')
      return
    }
    const query = `
      INSERT INTO avocado.data_accesstokens
        (interface, token, expiry, created_at, created_by, updated_at, updated_by)
      VALUES
        ($1, $2, $3, NOW(), $4, NOW(), $5)
    `
    const createdBy = this.user.name
    const values = [this.interface, token, expiry, createdBy, createdBy]
    try {
      await this.pgConnection.query(query, values)
    } catch (err) {
      this.logger.error('RmsAdapter: token insert failed', err)
    }
  }

  async requestNewToken () {
    if (!this.username || !this.password) {
      this.logger && this.logger.warn && this.logger.warn('RmsAdapter: client credentials not provided')
      return null
    }

    const body = JSON.stringify({
      username: this.username,
      password: this.password
    })

    try {
      const res = await this._rawFetch(this.authUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      })

      if (!res.ok) {
        return
      }

      const responseText = typeof res.text === 'function' ? await res.text() : ''
      let token = responseText.trim()
      try {
        const payload = JSON.parse(responseText)
        token = (payload && typeof payload.token === 'string') ? payload.token.trim() : ''
      } catch (error) {
        // do nothing, parsing failed so it is not a json - fallback to the text response
      }

      if (!token) {
        this.logger.error('RmsAdapter: auth response did not contain a token')
        return null
      }

      // RMS tokens are valid for 1 hour
      const expirySeconds = 3600

      // Subtract a small buffer of 60 seconds to refresh early
      const EXPIRY_BUFFER_SECS = 60

      const expiry = new Date(Date.now() + (expirySeconds - EXPIRY_BUFFER_SECS) * 1000)
      return { token, expiry }
    } catch (err) {
      this.logger.error('RmsAdapter: token request failed', err)
      return null
    }
  }

  async handleAuthErrorAndRetry (requestFn, maxRetries = 2) {
    let authRetries = 0

    while (authRetries < maxRetries) {
      try {
        const result = await requestFn()
        return result
      } catch (error) {
        if (error.status === 401 || error.status === 403 || (error.message && (error.message.includes('401') || error.message.includes('403')))) {
          authRetries++
          this.logger.warn(`RmsAdapter: 401 authentication error detected (attempt ${authRetries}/${maxRetries})`)

          if (authRetries >= maxRetries) {
            this.logger.error('RmsAdapter: Maximum auth retry attempts reached')
            throw new Error('Authentication failed after maximum retry attempts')
          }

          this.logger.info('RmsAdapter: Authentication error detected, retrying request')
        } else {
          throw error
        }
      }
    }
  }

  async confirmShipment (shipment, products) {
    const productToFetch = products.map(d => `product:${d}`)
    const productDocs = await this.api.product.getByIds(productToFetch)
    const productsById = keyBy(productDocs, '_id')

    const locationsToFetch = [shipment.destination.id, shipment.origin.id]
    const locationDocs = await this.api.location.getByIds(locationsToFetch)
    const locationsById = keyBy(locationDocs, '_id')

    const transformedShipment = transformRmsShipment(shipment, productsById, locationsById)

    const path = '/api/dataservices/delivery-confirmation'

    const authedFetch = await this.getAuthedFetch()

    this.logger.info('Making delivery confirmation call')

    await authedFetch({
      path,
      body: transformedShipment,
      method: 'POST'
    })

    this.logger.info('RMS delivery confirmation done')
  }

  async getBranches (options = {}) {
    return this.handleAuthErrorAndRetry(async () => {
      const { page = 0, size = 200, active } = options

      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('size', size.toString())

      if (typeof active === 'boolean') {
        params.append('active', active.toString())
      }

      const path = `/api/dataservices/branchmaster?${params.toString()}`

      const authedFetch = await this.getAuthedFetch()
      const response = await authedFetch({
        path,
        body: null,
        method: 'GET'
      })

      const data = await response.json()

      return data
    })
  }

  async getStockData (options = {}) {
    validateStockDataOptions(options)

    return this.handleAuthErrorAndRetry(async () => {
      const { page = 0, size = 5000, valuationArea, program } = options

      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('size', size.toString())

      if (valuationArea) {
        params.append('valuationArea', valuationArea.toString())
      }

      if (program) {
        params.append('program', program.toString())
      }

      const path = `/api/dataservices/stock?${params.toString()}`

      const authedFetch = await this.getAuthedFetch()
      const response = await authedFetch({
        path,
        body: null,
        method: 'GET'
      })

      const data = await response.json()

      return data
    })
  }

  async _fetchPaginatedBatch ({ path, size, maxConcurrency = 3, extraParams = {} }) {
    let currentPage = 0
    let hasMore = true
    const allResults = []

    while (hasMore) {
      const pages = Array.from({ length: maxConcurrency }, (_, i) => currentPage + i)

      const batchPromises = pages.map(async page => {
        const params = new URLSearchParams()
        params.append('page', page.toString())
        params.append('size', size.toString())

        for (const [key, value] of Object.entries(extraParams)) {
          if (value != null) params.append(key, value.toString())
        }

        const authedFetch = await this.getAuthedFetch()
        return authedFetch({ path: `${path}?${params.toString()}`, body: null, method: 'GET' })
          .then(response => response.json())
      })

      const batchResults = await Promise.all(batchPromises)
      const hasItems = batchResults.some(result => result.items && result.items.length > 0)
      const lastResult = batchResults[batchResults.length - 1]

      if (!hasItems || (lastResult.items && lastResult.items.length < size)) {
        hasMore = false
      }

      allResults.push(...batchResults.filter(result => result.items && result.items.length > 0))
      currentPage += maxConcurrency

      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
    }

    if (allResults.length === 0) {
      return { items: [], total: 0, page: 0, size, timestamp: new Date().toISOString() }
    }

    const combinedItems = allResults.flatMap(result => result.items || [])

    return {
      items: combinedItems,
      total: allResults[0].total || 0,
      page: 0,
      size: combinedItems.length,
      timestamp: new Date().toISOString(),
      batchInfo: {
        totalPages: allResults.length,
        totalRequests: allResults.length,
        concurrency: maxConcurrency
      }
    }
  }

  async getStockDataBatch (options = {}) {
    validateStockDataOptions(options)

    return this.handleAuthErrorAndRetry(async () => {
      const { size = 5000, valuationArea, program, maxConcurrency = 3 } = options
      const extraParams = {}
      if (valuationArea) extraParams.valuationArea = valuationArea
      if (program) extraParams.program = program
      return this._fetchPaginatedBatch({ path: '/api/dataservices/stock', size, maxConcurrency, extraParams })
    })
  }

  async getPurchaseOrdersDataBatch (options = {}) {
    return this.handleAuthErrorAndRetry(async () => {
      const { size = 15000, maxConcurrency = 3 } = options
      return this._fetchPaginatedBatch({ path: '/api/dataservices/purchase-orders', size, maxConcurrency })
    })
  }

  async getSalesOrdersDataBatch (options = {}) {
    // Required lazily so importing the fs-api barrel doesn't force csv-parse on
    // consumers (e.g. web) that never call this method.
    const parse = require('csv-parse/lib/sync')

    return this.handleAuthErrorAndRetry(async () => {
      const { dateFrom, dateTo, program } = options

      const params = new URLSearchParams()
      if (dateFrom) params.append('dateFrom', dateFrom)
      if (dateTo) params.append('dateTo', dateTo)
      if (program) params.append('program', program)

      const query = params.toString()
      // Use the flat CSV export rather than the header-list + per-order detail
      // endpoints: it returns every order line in a single streamed request
      // (avoiding N+1 detail calls) at the line-level grain our ingestion needs.
      const basePath = '/api/dataservices/sales-orders/export.csv'
      const path = query ? `${basePath}?${query}` : basePath

      const authedFetch = await this.getAuthedFetch()
      const response = await authedFetch({ path, body: null, method: 'GET' })
      const csvText = await response.text()

      const items = parse(csvText, {
        columns: true,
        skip_empty_lines: true,
        trim: true,
        bom: true
      })

      return {
        items,
        total: items.length,
        page: 0,
        size: items.length,
        timestamp: new Date().toISOString(),
        batchInfo: {
          totalLines: items.length,
          source: 'export.csv'
        }
      }
    })
  }

  async getProductPricesData () {
    return this.handleAuthErrorAndRetry(async () => {
      const path = '/api/integration/load-prices'
      const authedFetch = await this.getAuthedFetch()
      const response = await authedFetch({
        path,
        body: null,
        method: 'GET'
      })

      const data = await response.json()
      const items = Array.isArray(data) ? data : []
      const timestamp = new Date().toISOString()

      return {
        items,
        total: items.length,
        page: 0,
        timestamp
      }
    })
  }

  // the batches for getProductPricesDataBatch are not necessary at this point - whole data is returned in one response, there is no pagination/batching happening.
  // it has been added for consistency with how the stock data is being fetched + in case the prices data needs to be fetched in batches in the future.
  async getProductPricesDataBatch () {
    return this.handleAuthErrorAndRetry(async () => {
      const result = await this.getProductPricesData()
      return {
        ...result,
        batchInfo: {
          totalPages: 1,
          totalRequests: 1,
          concurrency: 1
        }
      }
    })
  }
}

module.exports = RmsAdapter
